import {
    allowNextNavigation,
    api,
    useIsPlatformHost,
    useZodForm,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Button,
    DateTime,
    Field,
    FormSheet,
    Input,
    type ListColumnDef,
    PageHeader,
    StatusBadge,
} from '@bemor/web-kit/ui';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import type { Tenant } from '@/api/types';
import { OpenTenant } from '@/components/open-tenant';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { useWrite } from '@/lib/queries';
import { brandName, legalName } from '@/lib/tenants';

const schema = z.object({
    slug: z
        .string()
        .regex(
            /^[a-z][a-z0-9-]{1,30}$/,
            'lower case, letters, digits and dashes',
        ),
    legal_name: z.string().min(2, 'The legal name'),
    display_name: z.string().min(2, 'The brand name'),
    descriptor_prefix: z.string().min(2).max(6, 'Two to six characters'),
});

const columns: ListColumnDef<Tenant>[] = [
    {
        key: 'name',
        label: 'Tenant',
        sortKey: 'name',
        boardCard: true,
        cell: (t) => (
            <Link
                to={`/tenants/${t.id}`}
                className="hover:underline"
                onClick={(e) => e.stopPropagation()}
            >
                <span className="font-medium">{brandName(t)}</span>
                <span className="text-muted-foreground ml-2 font-mono text-xs">
                    {t.slug}
                </span>
            </Link>
        ),
        locked: 'start',
    },
    {
        key: 'status',
        label: 'Status',
        sortKey: 'status',
        cell: (t) => <StatusBadge status={t.status} domain="tenant" />,
        groupable: { key: 'status', statusDomain: 'tenant' },
    },
    { key: 'legal', label: 'Legal name', cell: (t) => legalName(t) },
    {
        key: 'domains',
        label: 'Domains',
        cell: (t) => t.domains.length,
        noTruncate: true,
    },
    {
        key: 'activation',
        label: 'Activation',
        cell: (t) =>
            `${t.activation.filter((s) => s.state === 'done').length}/${t.activation.length} steps`,
        noTruncate: true,
    },
    {
        // BM-97: the tenant's Ledger: provisioned by the organization key, its active API keys (left out when Ledger could not say)
        key: 'ledger',
        label: 'Ledger',
        // BM-145: a failed run names its step; a run in progress says so
        cell: (t) =>
            t.ledger?.status === 'failed'
                ? `Failed: ${t.ledger.steps.find((s) => s.status === 'failed')?.name ?? 'provisioning'}`
                : t.ledger?.status === 'running'
                  ? 'Provisioning…'
                  : t.ledger?.status === 'provisioned'
                    ? [
                          t.ledger.environment === 'sandbox'
                              ? 'Sandbox'
                              : 'Live',
                          t.ledger.key_count === null
                              ? null
                              : `${t.ledger.key_count} key${t.ledger.key_count === 1 ? '' : 's'}`,
                          t.ledger.webhook ? null : 'no webhook',
                      ]
                          .filter((part): part is string => part !== null)
                          .join(' · ')
                    : 'Not provisioned',
        noTruncate: true,
    },
    {
        key: 'created_at',
        aliases: ['created'],
        label: 'Since',
        kind: 'datetime',
        sortKey: 'created_at',
        cell: (t) => <DateTime value={t.created_at} format="date" empty="—" />,
        noTruncate: true,
    },
];

/** BM-68: on the platform host every row opens the tenant's Admin or Ledger (a hand-off, see `OpenTenant`). */
const openColumn: ListColumnDef<Tenant> = {
    key: 'open',
    label: 'Actions',
    actions: true,
    cell: (t) => <OpenTenant tenant={t} menu name={brandName(t)} />,
    noTruncate: true,
    locked: 'end',
};

const columnsFor = (platformHost: boolean): ListColumnDef<Tenant>[] =>
    platformHost ? [...columns, openColumn] : columns;

const PATH = '/staff/tenants';
const PAGE_KEY = 'tenants';

/** The route's prefetch: the person's views, then the rows of the view they land on (BM-46 §2.0 + §2.4). */
export const tenantsPrefetch = adminListPrefetch(PAGE_KEY, PATH);

const tenantFilters = filterDefs(PATH, {
    q: 'Search',
    status: 'Status',
    range: 'Created',
});

export function TenantsPage() {
    const navigate = useNavigate();
    const platformHost = useIsPlatformHost();
    const pageColumns = useMemo(() => columnsFor(platformHost), [platformHost]);
    const { state, query } = useAdminList<Tenant>(PAGE_KEY, PATH, {
        columns: pageColumns,
        filters: tenantFilters,
    });
    const [open, setOpen] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const form = useZodForm(schema, {
        defaultValues: {
            slug: '',
            legal_name: '',
            display_name: '',
            descriptor_prefix: '',
        },
    });
    const create = useWrite(
        async (values: z.infer<typeof schema>) =>
            (
                await api().post<Tenant>('/staff/tenants', {
                    slug: values.slug,
                    legal: { name: values.legal_name },
                    brand: { display_name: values.display_name },
                    descriptor_prefix: values.descriptor_prefix,
                })
            ).data,
        ['/staff/tenants'],
    );
    return (
        <div>
            <PageHeader
                title="Tenants"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {
                                'White-label tenants of the platform: their domains, integrations and the activation checklist.'
                            }
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
                primaryAction={
                    <Button onClick={() => setOpen(true)}>New tenant</Button>
                }
            />
            <AdminListPage
                noun="tenants"
                state={state}
                query={query}
                rowKey={(t) => t.id}
                rowHref={(t) => `/tenants/${t.id}`}
                controls={{
                    q: { kind: 'search' },
                    status: { domain: 'tenant' },
                }}
            />
            {/* BM-84 (rule E.9, design 0.7.0 tenants): the new tenant in a sheet */}
            <FormSheet
                open={open}
                onOpenChange={(o) => {
                    setOpen(o);
                    if (!o) setError(null);
                }}
                size="sm"
                title="New tenant"
                submitLabel="Create tenant"
                busy={create.isPending}
                dirty={form.formState.isDirty}
                error={error}
                data-testid="tenant-form"
                onSubmit={form.handleSubmit((values) => {
                    setError(null);
                    create.mutate(values, {
                        onSuccess: (t) => {
                            // saved: the reset and the navigation happen in one handler, so tell the guard to let this one through
                            setOpen(false);
                            form.reset();
                            allowNextNavigation();
                            navigate(`/tenants/${t.id}`);
                        },
                        onError: setError,
                    });
                })}
            >
                <Field
                    id="slug"
                    label="Slug"
                    hint="Becomes <slug>.pay, .app, .admin and .api until the tenant verifies its own hostnames."
                    error={form.formState.errors.slug?.message}
                >
                    <Input
                        id="slug"
                        placeholder="acme"
                        {...form.register('slug')}
                    />
                </Field>
                <Field
                    id="display_name"
                    label="Brand name"
                    error={form.formState.errors.display_name?.message}
                >
                    <Input
                        id="display_name"
                        {...form.register('display_name')}
                    />
                </Field>
                <Field
                    id="legal_name"
                    label="Legal name"
                    error={form.formState.errors.legal_name?.message}
                >
                    <Input id="legal_name" {...form.register('legal_name')} />
                </Field>
                <Field
                    id="descriptor_prefix"
                    label="Descriptor prefix"
                    hint="What buyers see on their card statement before the merchant's name."
                    error={form.formState.errors.descriptor_prefix?.message}
                >
                    <Input
                        id="descriptor_prefix"
                        placeholder="ACME*"
                        {...form.register('descriptor_prefix')}
                    />
                </Field>
            </FormSheet>
        </div>
    );
}
